// Reusable grouped module selector with dependency auto-enable.
// Used by the Portal Presets editor and the Client module-configuration screen.
import { Lock } from "lucide-react";

function buildIndex(groups) {
    const idx = {};
    (groups || []).forEach((g) => g.modules.forEach((m) => { idx[m.id] = m; }));
    return idx;
}

export function resolveDeps(ids, index) {
    const resolved = new Set(ids);
    Object.values(index).forEach((m) => { if (m.core) resolved.add(m.id); });
    let changed = true;
    while (changed) {
        changed = false;
        [...resolved].forEach((id) => (index[id]?.deps || []).forEach((d) => {
            if (index[d] && !resolved.has(d)) { resolved.add(d); changed = true; }
        }));
    }
    return resolved;
}

export default function PortalModuleSelector({ catalog, value = [], onChange, readOnly = false, idPrefix = "module" }) {
    const groups = catalog?.groups || [];
    const index = buildIndex(groups);
    const selected = new Set(value);

    function toggle(id) {
        if (readOnly) return;
        const m = index[id];
        if (!m || m.core) return;
        const cur = new Set(value);
        if (cur.has(id)) cur.delete(id); else cur.add(id);
        const resolved = resolveDeps(cur, index);
        const added = [...resolved].filter((x) => !cur.has(x) && !index[x]?.core);
        onChange?.([...resolved], added);
    }

    return (
        <div className="space-y-4" data-testid={`${idPrefix}-selector`}>
            {groups.map((g) => (
                <div key={g.group}>
                    <div className="text-[10.5px] uppercase tracking-[.08em] font-bold mb-2" style={{ color: "#9A968F" }}>
                        {g.group}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {g.modules.map((m) => {
                            const on = selected.has(m.id) || m.core;
                            const locked = m.core || readOnly;
                            return (
                                <button key={m.id} type="button"
                                    data-testid={`${idPrefix}-toggle-${m.id}`}
                                    onClick={() => toggle(m.id)}
                                    disabled={m.core || readOnly}
                                    aria-pressed={on}
                                    className={`flex items-center gap-2.5 px-3 py-2 rounded-[9px] border text-left transition-colors ${on
                                        ? "bg-[#FBEFE5] border-[var(--brand-accent,_#C75A1B)]"
                                        : "bg-white border-[#E5E0DA] hover:border-[#9A968F]"} ${locked ? "cursor-default" : "cursor-pointer"}`}>
                                    <span className={`w-4 h-4 rounded-[5px] grid place-items-center flex-shrink-0 border ${on ? "text-white" : "border-[#D8D3CB]"}`}
                                        style={on ? { background: "var(--brand-accent, #C75A1B)", borderColor: "var(--brand-accent, #C75A1B)" } : {}}>
                                        {on && (m.core ? <Lock className="w-2.5 h-2.5" /> : <span className="text-[10px] font-bold leading-none">✓</span>)}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="text-[12.5px] font-semibold text-[#1F1F1F] block truncate">{m.label}</span>
                                        {m.core && <span className="text-[10px] text-[#9A968F]">Always enabled</span>}
                                        {!m.core && m.deps?.length > 0 && (
                                            <span className="text-[10px] text-[#9A968F]">needs {m.deps.length} supporting module{m.deps.length > 1 ? "s" : ""}</span>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
